import { useEffect, useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDebounce } from '@/hooks/useDebounce'
import { FiSearch, FiChevronLeft, FiChevronRight, FiChevronDown, FiX, FiZoomIn, FiZoomOut } from 'react-icons/fi'
import ClearingShipmentDetailsModal, { type ClearingShipmentData } from './ClearingShipmentDetailsModal'
import ClearingDocumentCell, { type ClearingDocItem } from './ClearingDocumentCell'
import { getClearingPendingUploadsSummary, getClearingRecords, updateClearingRecord, uploadClearingDocuments, getClearingDocumentViewUrl, type ClearingPendingUploadItem, type ClearingRecordApi } from '@/services/api/clearing'
import PageSizeSelect from '@/components/common/PageSizeSelect'
import { useCallback } from 'react'

interface ClearingDocument extends ClearingDocItem {}

interface DocumentsModalState {
  item: ClearingData
  type?: 'shipping' | 'clearing' | 'bank'
}

interface ClearingData {
  id: string
  shipmentId: string
  buyContract: string
  saleContract: string
  booking: string
  contractType: string
  billOfLading: string
  containerNumber: string
  shippingLine: string
  origin: string
  destination: string
  clearingAgent: string
  arrivalDate: string
  shippingDocuments: 'Upload' | 'Uploaded'
  documentStatus: string
  status: string
  clearingDocuments: 'Upload' | 'Uploaded'
  clearingCosts: string
  bankDocuments: 'Upload' | 'Uploaded'
  documentUploadCount: number
  documents: ClearingDocument[]
}

const CLEARING_AGENT_OPTIONS = ['DSV', 'CASPIAN FREIGHT', 'C STEINWEG BRIDGE'] as const

const ClearingStatus = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentPage, setCurrentPage] = useState(1)
  const [resultsPerPage, setResultsPerPage] = useState(10)
  const [statusFilter, setStatusFilter] = useState('Status')
  const [containerFilter, setContainerFilter] = useState('Container Number')
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '')
  const debouncedSearch = useDebounce(searchQuery, 400)
  const [isShipmentDetailsModalOpen, setIsShipmentDetailsModalOpen] = useState(false)
  const [selectedShipment, setSelectedShipment] = useState<ClearingShipmentData | null>(null)
  const [clearingData, setClearingData] = useState<ClearingData[]>([])
  const [totalResults, setTotalResults] = useState(0)
  const [isEditMode, setIsEditMode] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pendingUploadsVisible, setPendingUploadsVisible] = useState(false)
  const [pendingUploads, setPendingUploads] = useState<ClearingPendingUploadItem[]>([])
  const [pendingUploadsIndex, setPendingUploadsIndex] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingUpload, setPendingUpload] = useState<{ clearingId: string; type: 'shipping' | 'clearing' | 'bank' } | null>(null)
  const [uploadingClearingId, setUploadingClearingId] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [viewDocument, setViewDocument] = useState<{ clearingId: string; docId: string; docName: string } | null>(null)
  const [docViewerZoom, setDocViewerZoom] = useState(100)
  const [documentsModal, setDocumentsModal] = useState<DocumentsModalState | null>(null)
  const [docUploadStepIndex, setDocUploadStepIndex] = useState(0)
  const [containerNumbers, setContainerNumbers] = useState<string[]>([])
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [inlineEdits, setInlineEdits] = useState<Record<string, { clearingAgent?: string; clearingCosts?: string }>>({})
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null)
  const [totalPages, setTotalPages] = useState(1)
  const autoRefreshMs = 45000

  const handleNextPendingUpload = () => {
    setPendingUploadsIndex((prev) => (prev + 1) % pendingUploads.length)
  }

  const handlePrevPendingUpload = () => {
    setPendingUploadsIndex((prev) => (prev - 1 + pendingUploads.length) % pendingUploads.length)
  }

  const handleInlineChange = (id: string, field: 'clearingAgent' | 'clearingCosts', value: string) => {
    setInlineEdits((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }))
  }

  const saveInlineField = useCallback(async (id: string, field: 'clearingAgent' | 'clearingCosts') => {
    const edit = inlineEdits[id]
    if (!edit) return
    const value = edit[field]
    if (value === undefined) return

    try {
      if (field === 'clearingAgent') {
        await updateClearingRecord(id, { clearingAgent: value })
        setClearingData((rows) =>
          rows.map((r) => (r.id === id ? { ...r, clearingAgent: value || '—' } : r))
        )
      } else {
        const numericVal = value.replace(/[^0-9.]/g, '')
        await updateClearingRecord(id, { clearingCost: numericVal ? parseFloat(numericVal) : 0 })
        setClearingData((rows) =>
          rows.map((r) => (r.id === id ? { ...r, clearingCosts: numericVal ? `$${parseFloat(numericVal).toFixed(2)}` : '—' } : r))
        )
      }
    } catch {
      // keep existing value on failure
    }
    setInlineEdits((prev) => {
      const next = { ...prev }
      if (next[id]) {
        delete next[id][field]
        if (!Object.keys(next[id]).length) delete next[id]
      }
      return next
    })
  }, [inlineEdits])

  const formatClearingStatusLabel = (status: string) => {
    const s = (status || '').toLowerCase()
    if (s === 'in_progress') return 'In Progress'
    if (s === 'on_hold') return 'On Hold'
    if (s === 'cleared') return 'Cleared'
    if (s === 'pending') return 'Pending'
    return status
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
      case 'Paid':
      case 'Cleared':
        return { textColor: '#13AB5B', bgColor: '#E8F7EF' }
      case 'IN Draft':
      case 'On Hold':
        return { textColor: '#E93323', bgColor: '#FEE2E2' }
      case 'In Progress':
      case 'IN Coordination':
        return { textColor: '#3B82F6', bgColor: '#E0E7FF' }
      default:
        return { textColor: '#69697A', bgColor: '#F2F4F2' }
    }
  }

  const currentData = clearingData

  const pageItems = (() => {
    const items: (number | 'ellipsis')[] = []
    const maxButtons = 7

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) items.push(i)
      return items
    }

    const first = 1
    const last = totalPages
    const windowSize = 2
    const start = Math.max(currentPage - windowSize, 2)
    const end = Math.min(currentPage + windowSize, totalPages - 1)

    items.push(first)
    if (start > 2) items.push('ellipsis')
    for (let p = start; p <= end; p++) items.push(p)
    if (end < totalPages - 1) items.push('ellipsis')
    items.push(last)

    return items
  })()

  const mapClearingRecordToRow = (record: ClearingRecordApi): ClearingData => {
    const shipment = record.shipment ?? undefined

    const docs = record.documents ?? []
    const hasShippingDoc = docs.some((d) => d.type === 'shipping')
    const hasClearingDoc = docs.some((d) => d.type === 'clearing')
    const hasBankDoc = docs.some((d) => d.type === 'bank')
    const contractTypeDisplay = shipment?.buyContract ? 'Buy Contract' : shipment?.saleContract ? 'Sale Contract' : '—'
    return {
      id: record.id,
      shipmentId: shipment?.booking ?? record.shipmentId,
      buyContract: shipment?.buyContract ?? '—',
      saleContract: shipment?.saleContract ?? '—',
      booking: shipment?.booking ?? '—',
      contractType: contractTypeDisplay,
      billOfLading: shipment?.bl ?? '—',
      containerNumber: shipment?.containerNumber ?? '—',
      shippingLine: shipment?.carrier ?? '—',
      origin: shipment?.pol ?? '—',
      destination: shipment?.pod ?? '—',
      clearingAgent: record.clearingAgent ?? '—',
      arrivalDate: shipment?.eta ?? '—',
      shippingDocuments: hasShippingDoc ? 'Uploaded' : 'Upload',
      documentStatus: record.vat404ReceiptStatus ?? record.status ?? 'Pending',
      status: record.status ?? 'pending',
      clearingDocuments: hasClearingDoc ? 'Uploaded' : 'Upload',
      clearingCosts: record.clearingCost != null ? `$${Number(record.clearingCost).toFixed(2)}` : '—',
      bankDocuments: hasBankDoc ? 'Uploaded' : 'Upload',
      documentUploadCount: docs.length,
      documents: docs,
    }
  }

  const fetchContainerNumbers = async () => {
    try {
      const res = await getClearingRecords({ page: 1, limit: 500 })
      const numbers = res.clearingRecords
        .map((r) => r.shipment?.containerNumber)
        .filter((cn): cn is string => !!cn && cn !== '—')
      setContainerNumbers([...new Set(numbers)].sort())
    } catch {
      // keep existing list
    }
  }

  const fetchClearingData = async (overrides?: { page?: number }) => {
    try {
      setLoading(true)
      setFetchError(null)
      const page = overrides?.page ?? currentPage
      const params: {
        page: number
        limit: number
        status?: string
        containerNumber?: string
        search?: string
      } = {
        page,
        limit: resultsPerPage,
      }
      if (statusFilter !== 'Status') params.status = statusFilter
      if (containerFilter !== 'Container Number') params.containerNumber = containerFilter
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim()
      const [res, pendingRes] = await Promise.all([
        getClearingRecords(params),
        getClearingPendingUploadsSummary({
          status: params.status,
          containerNumber: params.containerNumber,
          search: params.search,
        }),
      ])
      setClearingData(res.clearingRecords.map(mapClearingRecordToRow))
      setPendingUploads(pendingRes.items)
      setPendingUploadsIndex((prev) => {
        if (pendingRes.items.length === 0) return 0
        return Math.min(prev, pendingRes.items.length - 1)
      })
      const totalFromApi = res.meta?.total
      const totalFallback = res.clearingRecords.length
      const nextTotal = typeof totalFromApi === 'number' && totalFromApi > 0 ? totalFromApi : totalFallback
      setTotalResults(nextTotal)
      const pagesFromApi = res.meta?.totalPages
      const computedPages = Math.max(1, Math.ceil(nextTotal / resultsPerPage))
      setTotalPages(typeof pagesFromApi === 'number' && pagesFromApi > 0 ? pagesFromApi : computedPages)
    } catch (err: unknown) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load clearing records')
      setPendingUploads([])
      setPendingUploadsIndex(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchContainerNumbers()
  }, [])

  useEffect(() => {
    fetchClearingData()
  }, [currentPage, statusFilter, containerFilter, debouncedSearch, resultsPerPage])

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void fetchClearingData()
      }
    }, autoRefreshMs)

    return () => window.clearInterval(intervalId)
  }, [currentPage, statusFilter, containerFilter, debouncedSearch, resultsPerPage])

  useEffect(() => {
    if (pendingUploads.length > 0 && !pendingUploadsVisible) {
      setPendingUploadsVisible(true)
    }
  }, [pendingUploads.length, pendingUploadsVisible])

  useEffect(() => {
    const q = searchParams.get('search')
    if (q !== null) setSearchQuery(q)
  }, [searchParams])

  // When opening from AI Actions (?highlight=clearingRecordId), show that record's details and highlight the row
  const highlightId = searchParams.get('highlight')
  useEffect(() => {
    if (!highlightId || loading || clearingData.length === 0) return
    const item = clearingData.find((r) => r.id === highlightId)
    if (!item) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('highlight')
        return next
      }, { replace: true })
      return
    }
    const shipmentDetails: ClearingShipmentData = {
      id: item.id,
      shipmentId: item.shipmentId,
      containerNumber: item.containerNumber,
      shippingLine: item.shippingLine,
      origin: item.origin,
      destination: item.destination,
      arrivalDate: item.arrivalDate,
      documentStatus: item.documentStatus,
      status: item.status,
      clearingCost: item.clearingCosts !== '—' ? item.clearingCosts : undefined,
    }
    setSelectedShipment(shipmentDetails)
    setIsEditMode(false)
    setIsShipmentDetailsModalOpen(true)
    setHighlightedRowId(highlightId)
    setTimeout(() => setHighlightedRowId(null), 4000)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('highlight')
      return next
    }, { replace: true })
    requestAnimationFrame(() => {
      const row = document.getElementById(`clearing-row-${highlightId}`)
      row?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })
  }, [highlightId, loading, clearingData, setSearchParams])

  useEffect(() => {
    if (currentData.length > 0 && docUploadStepIndex >= currentData.length) {
      setDocUploadStepIndex(Math.max(0, currentData.length - 1))
    }
  }, [currentData.length, docUploadStepIndex])

  const triggerUpload = (clearingId: string, type: 'shipping' | 'clearing' | 'bank') => {
    setUploadError(null)
    setPendingUpload({ clearingId, type })
    fileInputRef.current?.click()
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0 || !pendingUpload) {
      e.target.value = ''
      setPendingUpload(null)
      return
    }
    setUploadingClearingId(pendingUpload.clearingId)
    setUploadError(null)
    const uploadType = pendingUpload.type
    const clearingId = pendingUpload.clearingId
    try {
      await uploadClearingDocuments(clearingId, files, uploadType)
      await fetchClearingData()
    } catch (err: unknown) {
      setUploadError((err as Error)?.message || 'Upload failed')
    } finally {
      setUploadingClearingId(null)
      setPendingUpload(null)
      e.target.value = ''
    }
  }

  const openDocumentsModal = (item: ClearingData, type?: 'shipping' | 'clearing' | 'bank') => {
    setDocumentsModal({ item, type })
  }

  const docsForModal = documentsModal
    ? documentsModal.item.documents.filter(
        (d) => !documentsModal.type || d.type === documentsModal.type
      )
    : []

  const documentsModalTitle = (() => {
    if (!documentsModal?.type) return 'Documents uploaded'
    if (documentsModal.type === 'shipping') return 'Shipping documents'
    if (documentsModal.type === 'clearing') return 'Clearing documents'
    return 'Bank documents'
  })()

  const renderDocumentCell = (
    item: ClearingData,
    type: 'shipping' | 'clearing' | 'bank',
    docs: ClearingDocument[]
  ) => (
    <ClearingDocumentCell
      docs={docs}
      uploading={uploadingClearingId === item.id}
      onUpload={() => triggerUpload(item.id, type)}
      onViewList={() => openDocumentsModal(item, type)}
      onViewDoc={(doc) => {
        setDocViewerZoom(100)
        setViewDocument({ clearingId: item.id, docId: doc.id, docName: doc.name })
      }}
      uploadTitle={`Upload ${type} PDF(s) — select multiple files`}
    />
  )

  return (
    <div className="space-y-4 sm:space-y-6 lg:max-w-[calc(100%-200px)] lg:ml-auto lg:mr-0" style={{ maxWidth: '100%' }}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />
      {uploadError && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700"
          style={{ fontFamily: 'Lexend Deca' }}
        >
          {uploadError}
          <button
            type="button"
            onClick={() => setUploadError(null)}
            className="ml-2 underline"
          >
            Dismiss
          </button>
        </div>
      )}
      {fetchError && (
        <div
          className="rounded-lg border flex items-start gap-3 px-4 py-3"
          style={{ fontFamily: 'Lexend Deca', backgroundColor: '#FEE2E2', borderColor: '#FECACA' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E93323" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div style={{ flex: 1, fontSize: '13px', lineHeight: '20px', color: '#991B1B' }}>{fetchError}</div>
          <button onClick={() => setFetchError(null)} style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: '#991B1B', opacity: 0.5, padding: '2px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
      )}
      {pendingUploadsVisible && pendingUploads.length > 0 && (
        <div className="flex justify-end">
          <div
            className="bg-white rounded-lg border border-gray-200 relative overflow-hidden"
            style={{
              fontFamily: 'Lexend Deca',
              width: '369px',
              height: '127px',
              padding: '16px',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                backgroundColor: '#EEC233',
              }}
            />
            <button
              onClick={() => setPendingUploadsVisible(false)}
              className="absolute top-3 right-3 flex items-center justify-center hover:opacity-80 transition-opacity"
              style={{ width: '24px', height: '24px' }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10.5" stroke="#212126" strokeWidth="1.5" fill="none" />
                <line x1="9.17" y1="9.17" x2="14.83" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="14.83" y1="9.17" x2="9.17" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>

            <div className="flex flex-col h-full justify-between" style={{ paddingRight: '8px' }}>
              <div className="flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#69697A' }}>
                    {pendingUploadsIndex + 1}/{pendingUploads.length}
                  </span>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center rounded flex-shrink-0" style={{ width: '24px', height: '24px', backgroundColor: '#FEF9EB' }}>
                    <img src="/images/danger.svg" alt="Warning" style={{ width: '16px', height: '16px' }} />
                  </div>
                  <div style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#212126' }}>
                    {pendingUploads.length} Pending Uploads
                  </div>
                </div>

                <div className="flex items-center gap-2" style={{ marginTop: '8px', marginLeft: '0px' }}>
                  <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#69697A' }}>
                    {pendingUploads[pendingUploadsIndex]?.clearingRef || pendingUploads[pendingUploadsIndex]?.clearingId.slice(0, 8)} - {pendingUploads[pendingUploadsIndex]?.text}
                  </span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12L10 8L6 4" stroke="#69697A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>

              <div className="flex items-center justify-start gap-2" style={{ marginTop: '8px', marginBottom: '8px' }}>
                <button onClick={handlePrevPendingUpload} className="flex items-center justify-center hover:opacity-80 transition-opacity" style={{ width: '24px', height: '24px' }}>
                  <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.75 3.5L5.25 7L8.75 10.5" stroke="#69697A" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button onClick={handleNextPendingUpload} className="flex items-center justify-center hover:opacity-80 transition-opacity" style={{ width: '24px', height: '24px' }}>
                  <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.25 3.5L8.75 7L5.25 10.5" stroke="#69697A" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div>
        {/* First Row: Title and Breadcrumbs on same level */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
          <h1
            className="text-lg sm:text-xl"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '18px',
              lineHeight: '32px',
              letterSpacing: '0%',
              color: '#212126'
            }}
          >
            Clearing Status 
          </h1>
          
          {/* Breadcrumbs */}
          <span
            className="text-xs sm:text-sm"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '12px',
              lineHeight: '20px',
              letterSpacing: '0%',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ color: '#69697A', margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Clearing Status</span>
          </span>
        </div>

        
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Search and Filters - Above Table */}
        <div className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            {/* Search Bar */}
            <div className="relative flex-1 w-full sm:max-w-md">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search shipments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  height: '40px'
                }}
              />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
              {/* Status Filter */}
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#050521',
                    height: '40px',
                    minWidth: '120px'
                  }}
                >
                  <option>Status</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="cleared">Cleared</option>
                </select>
                <FiChevronDown 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{
                    width: '16px',
                    height: '16px',
                    opacity: 0.8,
                    color: '#050521'
                  }}
                />
              </div>

              {/* Container Number Filter */}
              <div className="relative">
                <select
                  value={containerFilter}
                  onChange={(e) => setContainerFilter(e.target.value)}
                  className="px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#050521',
                    height: '40px',
                    minWidth: '160px'
                  }}
                >
                  <option>Container Number</option>
                  {containerNumbers.map((cn) => (
                    <option key={cn} value={cn}>{cn}</option>
                  ))}
                </select>
                <FiChevronDown 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{
                    width: '16px',
                    height: '16px',
                    opacity: 0.8,
                    color: '#050521'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto px-2 sm:px-4" style={{ minWidth: '0' }}>
          <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '2000px' }}>
            <thead>
              <tr>
                {[
                  'Buy Contract', 'Sale Contract', 'Booking', 'Bill of Lading', 'Container Number',
                  'Shipping Line', 'Origin', 'Destination', 'Clearing Agent',
                  'Arrival Date', 'Shipping documents', 'Document Status', 'Clearing Documents',
                  'Clearing Status', 'Clearing Costs', 'Bank Documents', 'Action'
                ].map((header, idx, arr) => (
                  <th
                    key={header}
                    className="text-left py-2 sm:py-3 text-xs sm:text-sm"
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontSize: '11px',
                      lineHeight: '20px',
                      color: '#69697A',
                      paddingLeft: '12px',
                      paddingRight: '12px',
                      borderTop: '1px solid #E9E9EA',
                      borderBottom: '1px solid #E9E9EA',
                      whiteSpace: 'nowrap',
                      ...(header === 'Clearing Agent' ? { minWidth: '220px' } : {}),
                      ...(idx === 0 ? { borderLeft: '1px solid #E9E9EA', borderTopLeftRadius: '8px' } : {}),
                      ...(idx === arr.length - 1 ? { borderRight: '1px solid #E9E9EA', borderTopRightRadius: '8px' } : {}),
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={17} className="py-8 text-center" style={{ fontFamily: 'Lexend Deca', fontSize: '14px', color: '#69697A' }}>
                    Loading…
                  </td>
                </tr>
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan={17} className="py-8 text-center" style={{ fontFamily: 'Lexend Deca', fontSize: '14px', color: '#69697A' }}>
                    No clearing records. Add one with New Shipment.
                  </td>
                </tr>
              ) : (
              <>
              {currentData.map((item) => {
                const docStatusColors = getStatusColor(item.documentStatus)
                const clearingStatusLabel = formatClearingStatusLabel(item.status)
                const statusColors = getStatusColor(clearingStatusLabel)
                const bankDocs = item.documents.filter((d) => d.type === 'bank')
                const shippingDocs = item.documents.filter((d) => d.type === 'shipping')
                const clearingDocs = item.documents.filter((d) => d.type === 'clearing')

                const textCellStyle = { fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' as const }

                return (
                  <tr
                    id={`clearing-row-${item.id}`}
                    key={item.id}
                    className={`hover:bg-gray-50 transition-colors ${highlightedRowId === item.id ? 'bg-blue-50 ring-1 ring-blue-200 ring-inset' : ''}`}
                  >
                    {/* Buy Contract */}
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ ...textCellStyle, borderLeft: '1px solid #E9E9EA' }}>
                      {item.buyContract}
                    </td>
                    {/* Sale Contract */}
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={textCellStyle}>
                      {item.saleContract}
                    </td>
                    {/* Booking */}
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={textCellStyle}>
                      {item.booking}
                    </td>
                    {/* Bill of Lading */}
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={textCellStyle}>
                      {item.billOfLading}
                    </td>
                    {/* Container Number */}
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={textCellStyle}>
                      {item.containerNumber}
                    </td>
                    {/* Shipping Line */}
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={textCellStyle}>
                      {item.shippingLine}
                    </td>
                    {/* Origin */}
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={textCellStyle}>
                      {item.origin}
                    </td>
                    {/* Destination */}
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={textCellStyle}>
                      {item.destination}
                    </td>
                    {/* Clearing Agent – inline dropdown */}
                    <td className="py-2 sm:py-3" style={{ ...textCellStyle, padding: '4px 8px', minWidth: '220px' }}>
                      <div className="relative">
                        {(() => {
                          const currentAgent = inlineEdits[item.id]?.clearingAgent ?? (item.clearingAgent === '—' ? '' : item.clearingAgent)
                          const hasCustomValue = !!currentAgent && !CLEARING_AGENT_OPTIONS.includes(currentAgent as typeof CLEARING_AGENT_OPTIONS[number])
                          return (
                            <select
                              value={currentAgent}
                              onChange={(e) => {
                                handleInlineChange(item.id, 'clearingAgent', e.target.value)
                                void saveInlineField(item.id, 'clearingAgent')
                              }}
                              className="appearance-none w-full"
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontSize: '13px',
                                lineHeight: '22px',
                                color: currentAgent ? '#212126' : '#69697A',
                                height: '32px',
                                minWidth: '204px',
                                padding: '4px 28px 4px 8px',
                                border: '1px solid #E9E9EA',
                                borderRadius: '6px',
                                backgroundColor: '#FFFFFF',
                                outline: 'none',
                              }}
                            >
                              <option value="">Add clearing agent</option>
                              {hasCustomValue && <option value={currentAgent}>{currentAgent}</option>}
                              {CLEARING_AGENT_OPTIONS.map((agent) => (
                                <option key={agent} value={agent}>{agent}</option>
                              ))}
                            </select>
                          )
                        })()}
                        <FiChevronDown
                          className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                          style={{ width: '14px', height: '14px', color: '#69697A' }}
                        />
                      </div>
                    </td>
                    {/* Arrival Date */}
                    <td className="py-2 sm:py-3 text-xs sm:text-sm" style={textCellStyle}>
                      {item.arrivalDate}
                    </td>
                    {/* Shipping Documents */}
                    <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA' }}>
                      {renderDocumentCell(item, 'shipping', shippingDocs)}
                    </td>
                    {/* Document Status */}
                    <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA' }}>
                      <span
                        className="inline-block text-xs font-medium"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 500,
                          fontSize: '12px',
                          lineHeight: '20px',
                          color: docStatusColors.textColor,
                          backgroundColor: docStatusColors.bgColor,
                          borderRadius: '6px',
                          padding: '4px 12px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.documentStatus}
                      </span>
                    </td>
                    {/* Clearing Documents */}
                    <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA' }}>
                      {renderDocumentCell(item, 'clearing', clearingDocs)}
                    </td>
                    {/* Clearing Status */}
                    <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA' }}>
                      <span
                        className="inline-block text-xs font-medium"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 500,
                          fontSize: '11px',
                          lineHeight: '20px',
                          color: statusColors.textColor,
                          backgroundColor: statusColors.bgColor,
                          borderRadius: '6px',
                          padding: '4px 10px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {clearingStatusLabel}
                      </span>
                    </td>
                    {/* Clearing Costs – inline editable */}
                    <td className="py-2 sm:py-3" style={{ ...textCellStyle, padding: '4px 8px' }}>
                      <input
                        type="text"
                        value={inlineEdits[item.id]?.clearingCosts ?? (item.clearingCosts === '—' ? '' : item.clearingCosts.replace(/^\$/, ''))}
                        onChange={(e) => handleInlineChange(item.id, 'clearingCosts', e.target.value)}
                        onBlur={() => saveInlineField(item.id, 'clearingCosts')}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                        placeholder="0.00"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontSize: '13px',
                          lineHeight: '22px',
                          color: '#212126',
                          width: '100%',
                          height: '32px',
                          padding: '4px 8px',
                          border: '1px solid #E9E9EA',
                          borderRadius: '6px',
                          backgroundColor: '#FFFFFF',
                          outline: 'none',
                        }}
                      />
                    </td>
                    {/* Bank Documents */}
                    <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA' }}>
                      {renderDocumentCell(item, 'bank', bankDocs)}
                    </td>
                    {/* Action */}
                    <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderRight: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA' }}>
                      <div className="flex items-center gap-1 sm:gap-2">
                        <button
                          className="flex items-center justify-center hover:opacity-80 rounded transition-colors"
                          title="Edit"
                          onClick={() => {
                            const shipmentDetails: ClearingShipmentData = {
                              id: item.id,
                              shipmentId: item.shipmentId,
                              containerNumber: item.containerNumber,
                              shippingLine: item.shippingLine,
                              origin: item.origin,
                              destination: item.destination,
                              arrivalDate: item.arrivalDate,
                              documentStatus: item.documentStatus,
                              status: item.status,
                              clearingCost: item.clearingCosts !== '—' ? item.clearingCosts : undefined,
                            }
                            setSelectedShipment(shipmentDetails)
                            setIsEditMode(true)
                            setIsShipmentDetailsModalOpen(true)
                          }}
                          style={{ backgroundColor: '#FEF9EB', width: '24px', height: '24px', padding: '0' }}
                        >
                          <img src="/images/edit.svg" alt="Edit" style={{ width: '14px', height: '14px' }} />
                        </button>
                        <button
                          className="flex items-center justify-center hover:opacity-80 rounded transition-colors"
                          title="View"
                          onClick={() => {
                            const shipmentDetails: ClearingShipmentData = {
                              id: item.id,
                              shipmentId: item.shipmentId,
                              containerNumber: item.containerNumber,
                              shippingLine: item.shippingLine,
                              origin: item.origin,
                              destination: item.destination,
                              arrivalDate: item.arrivalDate,
                              documentStatus: item.documentStatus,
                              status: item.status,
                              clearingCost: item.clearingCosts !== '—' ? item.clearingCosts : undefined,
                            }
                            setSelectedShipment(shipmentDetails)
                            setIsEditMode(false)
                            setIsShipmentDetailsModalOpen(true)
                          }}
                          style={{ backgroundColor: '#EAF1FF', width: '24px', height: '24px', padding: '0' }}
                        >
                          <img src="/images/eye.svg" alt="View" style={{ width: '14px', height: '14px' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
              </>
              )}
              {/* Pagination Row */}
              <tr>
                <td colSpan={17} className="py-3 sm:py-4 px-2 sm:px-4" style={{ paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderLeft: '1px solid #E9E9EA', borderRight: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                    <span
                      className="text-xs sm:text-sm"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '22px',
                        color: '#69697A'
                      }}
                    >
                      Showing {currentData.length} of {totalResults} Results
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                      <PageSizeSelect
                        value={resultsPerPage}
                        onChange={(size) => {
                          setResultsPerPage(size)
                          setCurrentPage(1)
                        }}
                      />
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '22px',
                          color: '#212126',
                          border: '1px solid #E9E9EA',
                          backgroundColor: 'transparent'
                        }}
                      >
                        <FiChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Previous</span>
                      </button>
                      {pageItems.map((item, idx) =>
                        item === 'ellipsis' ? (
                          <span
                            key={`ellipsis-${idx}`}
                            className="px-2 select-none"
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '22px',
                              color: '#69697A',
                            }}
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={item}
                            onClick={() => setCurrentPage(item)}
                            className="px-3 py-1 rounded hover:opacity-90 transition-opacity"
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '22px',
                              color: currentPage === item ? '#FFFFFF' : '#212126',
                              backgroundColor: currentPage === item ? '#212126' : 'transparent',
                              border: '1px solid #E9E9EA'
                            }}
                          >
                            {item}
                          </button>
                        )
                      )}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '22px',
                          color: '#212126',
                          border: '1px solid #E9E9EA',
                          backgroundColor: 'transparent'
                        }}
                      >
                        <span className="hidden sm:inline">Next</span>
                        <FiChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Shipment Details Modal */}
      <ClearingShipmentDetailsModal 
        isOpen={isShipmentDetailsModalOpen} 
        onClose={() => {
          setIsShipmentDetailsModalOpen(false)
          setSelectedShipment(null)
          setIsEditMode(false)
          setHighlightedRowId(null)
        }}
        shipmentData={selectedShipment || undefined}
        mode={isEditMode ? 'edit' : 'view'}
        onUploadDocument={(type) => {
          if (!selectedShipment?.id) return
          triggerUpload(selectedShipment.id, type)
        }}
        isUploading={uploadingClearingId === selectedShipment?.id}
        documents={clearingData.find((r) => r.id === selectedShipment?.id)?.documents}
        onViewDocuments={(type) => {
          const row = clearingData.find((r) => r.id === selectedShipment?.id)
          if (row) openDocumentsModal(row, type)
        }}
        onSave={async (updated) => {
          await updateClearingRecord(updated.id, {
            status: updated.status,
            vat404ReceiptStatus: updated.documentStatus,
            ...(updated.clearingCost !== undefined && updated.clearingCost !== '' && { clearingCost: updated.clearingCost }),
          })
          setClearingData((rows) =>
            rows.map((row) =>
              row.id === updated.id
                ? {
                    ...row,
                    documentStatus: updated.documentStatus,
                    status: updated.status,
                    clearingCosts: updated.clearingCost !== undefined ? (updated.clearingCost || '—') : row.clearingCosts,
                  }
                : row
            )
          )
          setSelectedShipment(updated)
          setIsShipmentDetailsModalOpen(false)
          setIsEditMode(false)
        }}
      />

      {/* Documents uploaded list popup */}
      {documentsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setDocumentsModal(null)}
          role="dialog"
          aria-modal="true"
          aria-label={documentsModalTitle}
        >
          <div
            className="bg-white rounded-lg shadow-xl flex flex-col overflow-hidden"
            style={{
              width: '100%',
              maxWidth: '480px',
              maxHeight: '80vh',
              fontFamily: 'Lexend Deca',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between flex-shrink-0 px-4 py-3 border-b"
              style={{ borderColor: '#E9E9EA', backgroundColor: '#F2F4F2' }}
            >
              <span style={{ fontWeight: 600, fontSize: '14px', color: '#212126' }}>
                {documentsModalTitle}
              </span>
              <button
                type="button"
                onClick={() => setDocumentsModal(null)}
                className="flex items-center justify-center hover:opacity-80 transition-opacity rounded-full border border-gray-300"
                style={{ width: '32px', height: '32px', color: '#69697A' }}
                aria-label="Close"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto px-4 py-3">
              {docsForModal.length === 0 ? (
                <p style={{ fontFamily: 'Lexend Deca', fontSize: '13px', color: '#69697A' }}>
                  No documents uploaded yet.
                </p>
              ) : (
                <ul className="list-none p-0 m-0 space-y-2">
                  {docsForModal.map((doc) => (
                    <li
                      key={doc.id}
                      className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg border"
                      style={{ borderColor: '#E9E9EA', backgroundColor: '#FAFAFA' }}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <img
                          src="/images/document-text.svg"
                          alt=""
                          style={{ width: '16px', height: '16px', flexShrink: 0 }}
                        />
                        <span
                          className="truncate"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontSize: '13px',
                            color: '#212126',
                          }}
                          title={doc.name}
                        >
                          {doc.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setDocViewerZoom(100)
                          setViewDocument({
                            clearingId: documentsModal.item.id,
                            docId: doc.id,
                            docName: doc.name,
                          })
                          setDocumentsModal(null)
                        }}
                        className="flex-shrink-0 hover:opacity-90 transition-opacity rounded px-3 py-1.5 text-xs font-medium"
                        style={{
                          fontFamily: 'Lexend Deca',
                          color: '#3B82F6',
                          backgroundColor: '#E0E7FF',
                          border: '1px solid #3B82F6',
                        }}
                      >
                        View
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {documentsModal.type && (
              <div className="flex-shrink-0 px-4 py-3 border-t" style={{ borderColor: '#E9E9EA' }}>
                <button
                  type="button"
                  onClick={() => {
                    triggerUpload(documentsModal.item.id, documentsModal.type!)
                    setDocumentsModal(null)
                  }}
                  disabled={uploadingClearingId === documentsModal.item.id}
                  className="px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
                  style={{ fontFamily: 'Lexend Deca', backgroundColor: '#212126', color: '#FFFFFF' }}
                >
                  Add more PDF(s)
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Document viewer popup */}
      {viewDocument && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setViewDocument(null)}
          role="dialog"
          aria-modal="true"
          aria-label="View document"
        >
          <div
            className="bg-white rounded-lg shadow-xl flex flex-col overflow-hidden"
            style={{
              width: '95vw',
              height: '95vh',
              maxWidth: '1200px',
              fontFamily: 'Lexend Deca',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between flex-shrink-0 px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 gap-2"
              style={{ backgroundColor: '#F2F4F2' }}
            >
              <span
                className="truncate flex-1 min-w-0"
                style={{
                  fontWeight: 600,
                  fontSize: '13px',
                  color: '#212126',
                }}
                title={viewDocument.docName}
              >
                {viewDocument.docName}
              </span>
              <div className="flex items-center gap-1 flex-shrink-0">
                <span
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontSize: '12px',
                    color: '#69697A',
                    minWidth: '36px',
                    textAlign: 'center',
                  }}
                >
                  {docViewerZoom}%
                </span>
                <button
                  type="button"
                  onClick={() => setDocViewerZoom((z) => Math.max(50, z - 25))}
                  className="flex items-center justify-center hover:opacity-80 transition-opacity rounded border border-gray-300"
                  style={{ width: '32px', height: '32px', color: '#69697A' }}
                  aria-label="Zoom out"
                  title="Zoom out"
                >
                  <FiZoomOut className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setDocViewerZoom((z) => Math.min(200, z + 25))}
                  className="flex items-center justify-center hover:opacity-80 transition-opacity rounded border border-gray-300"
                  style={{ width: '32px', height: '32px', color: '#69697A' }}
                  aria-label="Zoom in"
                  title="Zoom in"
                >
                  <FiZoomIn className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewDocument(null)}
                  className="flex items-center justify-center hover:opacity-80 transition-opacity rounded-full border border-gray-300 ml-1"
                  style={{ width: '32px', height: '32px', color: '#69697A' }}
                  aria-label="Close"
                  title="Close"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div
              className="flex-1 min-h-0 overflow-auto p-2 sm:p-4"
              style={{ backgroundColor: '#525659' }}
            >
              <div
                className="w-full flex justify-center"
                style={{
                  transform: `scale(${docViewerZoom / 100})`,
                  transformOrigin: 'top center',
                  height: 'calc(95vh - 80px)',
                  minHeight: '400px',
                }}
              >
                <iframe
                  title={viewDocument.docName}
                  src={getClearingDocumentViewUrl(viewDocument.clearingId, viewDocument.docId)}
                  className="border border-gray-300 rounded bg-white shadow-sm flex-1"
                  style={{
                    width: '100%',
                    height: 'calc(95vh - 80px)',
                    minHeight: '400px',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClearingStatus

